"use client";

import React, { useState, useEffect, useMemo } from "react";
import { MotionWrapper } from "../MotionWrapper";
import { ChevronDown, Code, Rocket } from "lucide-react";
import Link from "next/link";
import { smoothScrollTo } from "@/utils/scroll";

function HeroSection() {
  const [currentText, setCurrentText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);
  const [textIndex, setTextIndex] = useState(0);

  const texts = useMemo(
    () => ["software engineer", "lead developer", "tech enthusiast"],
    []
  );

  useEffect(() => {
    const typeSpeed = isDeleting ? 50 : 100;
    const deleteSpeed = 50;

    const timer = setTimeout(
      () => {
        if (!isDeleting && currentText === texts[textIndex]) {
          setTimeout(() => setIsDeleting(true), 2000);
        } else if (isDeleting && currentText === "") {
          setIsDeleting(false);
          setTextIndex((prev) => (prev + 1) % texts.length);
        } else {
          setCurrentText(
            isDeleting
              ? texts[textIndex].slice(0, currentText.length - 1)
              : texts[textIndex].slice(0, currentText.length + 1)
          );
        }
      },
      isDeleting ? deleteSpeed : typeSpeed
    );

    return () => clearTimeout(timer);
  }, [currentText, isDeleting, textIndex, texts]);

  const handleScroll = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    smoothScrollTo(id);
  };

  return (
    <main className="relative z-10 flex flex-col items-center justify-center min-h-[90vh] px-4 pt-20">
      <MotionWrapper
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.8 }}
        className="text-center max-w-4xl relative z-10"
      >
        <h1 className="text-5xl md:text-7xl font-bold mb-4">
          Hi, I am{" "}
          <span className="relative">
            Thiemo
            <span className="text-[#FFD700] absolute animate-pulse">.</span>
          </span>
        </h1>

        <div className="text-xl md:text-2xl text-gray-300 mt-6 mb-4 h-8">
          A passionate{" "}
          <span className="text-[#FFD700] font-medium">
            {currentText}
            <span className="animate-pulse">|</span>
          </span>
        </div>

        <p className="text-lg md:text-xl text-gray-400 mb-12 max-w-2xl mx-auto">
          Exploring the digital universe and crafting innovative solutions that
          bridge technology and creativity.
        </p>

        <div className="flex flex-wrap justify-center gap-6 mt-8">
          <MotionWrapper
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
            className="group"
          >
            <Link
              className="px-8 py-4 bg-gradient-to-r from-[#FFD700] to-[#FFA500] text-black font-medium rounded-full flex items-center shadow-lg shadow-[#FFD700]/25 hover:shadow-[#FFD700]/40 transition-all duration-300"
              href="#clients"
              onClick={(e) => handleScroll(e, "clients")}
            >
              <Code
                className="mr-2 group-hover:rotate-12 transition-transform duration-300"
                size={20}
              />
              View Clients
            </Link>
          </MotionWrapper>

          <MotionWrapper
            whileHover={{ scale: 1.05, y: -2 }}
            whileTap={{ scale: 0.95 }}
            className="group"
          >
            <Link
              className="px-8 py-4 bg-transparent border-2 border-[#FFD700] text-[#FFD700] font-medium rounded-full flex items-center hover:bg-[#FFD700] hover:text-black transition-all duration-300"
              href="mailto:thiemo@lunaty.net"
            >
              <Rocket
                className="mr-2 group-hover:rotate-12 transition-transform duration-300"
                size={20}
              />
              Contact Me
            </Link>
          </MotionWrapper>
        </div>
      </MotionWrapper>

      {/* Enhanced scroll indicator */}
      <MotionWrapper
        animate={{ y: [0, 10, 0] }}
        transition={{ repeat: Number.POSITIVE_INFINITY, duration: 2 }}
        className="absolute bottom-10 flex flex-col items-center"
      >
        <ChevronDown className="text-[#FFD700]" size={32} />
      </MotionWrapper>
    </main>
  );
}

export default HeroSection;
